import { DELIVERY_TYPES, CAMEL_DELIVERY_TYPES } from '~constants/delivery';
import { DISPATCH_METHODS, ORDER_CHANNELS } from '~constants/orders';

export const clientRows = (item, dispatchMethod) => [
  {
    text: 'clientName',
    value: item.userName
  },
  {
    text: 'phone',
    value: item.userPhone
  },
  {
    text: 'address',
    value: item.userAddress
  },
  {
    text: 'apartmentDetails',
    value: item.apartmentDetails
  },
  {
    text: 'deliveryNotes',
    value: item.dispatchNote,
    hide: dispatchMethod === DISPATCH_METHODS.in_store
  }
];

export const billingRows = (item) => [
  {
    text: 'billingType',
    value: item.billingType
  },
  {
    text: 'businessName',
    value: item.businessName
  },
  {
    text: 'documentType',
    value: item.documentType
  },
  {
    text: 'documentId',
    value: item.documentId
  },
  {
    text: 'businessPhone',
    value: item.businessPhone
  },
  ...(item.economicActivityCode
    ? [{ text: 'economicActivityCode', value: item.economicActivityCode }]
    : []),
  ...(item.taxFree ? [{ text: 'taxFree', value: null, isTaxFree: true }] : [])
];

const getDeliveryDetails = (item, deliveryMethods) => {
  const toLowerCamel = (s) => (s ? s.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) : '');
  const deliveries = Array.isArray(item.deliveries) ? item.deliveries : [];
  const hasTopLevel = typeof item.delivery === 'number';

  if (hasTopLevel && deliveries.length > 0) {
    const typePascal = deliveries[0].type;
    const method = deliveryMethods.find((m) => CAMEL_DELIVERY_TYPES[m?.deliveryType] === typePascal);
    const key = toLowerCamel(method?.deliveryType);
    const ext = method?.externalDeliveryCofinance?.[key]?.deliveryFee ?? 0;
    let totalCofinanced = 0;
    let deliveryFee = item.delivery;

    if (ext > 0 && deliveries[0]?.priceDetails) {
      totalCofinanced = deliveries[0].priceDetails.totalCofinanced ?? 0;
      deliveryFee = deliveries[0].total;
    }

    return {
      delivery: deliveryFee,
      serviceFee: 0,
      totalCofinanced,
      isExternal: false
    };
  }

  if (!deliveries.length) return { delivery: 0, serviceFee: 0, totalCofinanced: 0, isExternal: false };

  const { total = 0, type, priceDetails = {} } = deliveries[0];
  const { serviceFee = 0, totalCofinanced = 0 } = priceDetails;
  return { delivery: total, serviceFee, totalCofinanced, isExternal: type !== DELIVERY_TYPES.OWN };
};

export const priceRows = (item, loyaltyProgram, deliveryMethods) => {
  const couponAmount = item.coupon?.value ?? 0;
  const pointsToUseAmount = item.pointsToUse ?? 0;
  const itemDiscountAmount = item.discount ?? 0;
  const uberDiscount = Math.round(itemDiscountAmount - couponAmount - pointsToUseAmount);
  const orderTip = item.tip ?? 0;
  const hasTip = orderTip > 0;
  const isUberEats = item.channel === ORDER_CHANNELS.UBER_EATS;

  const {
    delivery,
    serviceFee,
    totalCofinanced,
    isExternal: isExternalDelivery
  } = getDeliveryDetails(item, deliveryMethods);

  const rows = {
    subtotal: {
      text: 'subtotal',
      value: item.totalPrice + item.discount - item.delivery
    },
    ...(couponAmount > 0 && {
      couponDiscount: {
        text: 'couponDiscount',
        value: couponAmount,
        discount: true
      }
    }),
    ...(pointsToUseAmount > 0 && {
      pointsUsed: {
        text: 'pointsUsed',
        value: pointsToUseAmount,
        noMoneySymbol: false,
        discount: true,
        metadata: loyaltyProgram ? { loyaltyProgramName: loyaltyProgram.name } : {}
      }
    }),
    ...(isUberEats && {
      uberDiscount: {
        text: 'uberDiscount',
        value: uberDiscount,
        noMoneySymbol: false,
        discount: true
      }
    }),
    ...(delivery > 0 && {
      deliveryFee: {
        text: 'Delivery',
        value: delivery
      }
    }),
    ...(serviceFee > 0 && {
      serviceFee: {
        text: 'serviceFee',
        value: serviceFee
      }
    }),
    ...(hasTip && {
      tip: {
        text: 'tip',
        value: orderTip
      }
    }),
    ...(totalCofinanced > 0 && {
      totalCofinanced: {
        text: 'totalCofinanced',
        value: totalCofinanced,
        discount: true
      }
    }),
    total: {
      hasDivider: true,
      text: 'total',
      value: hasTip ? item.totalWithTip : item.totalPrice,
      bold: isExternalDelivery
    }
  };

  return rows;
};
